import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  cn,
  MessageSquareIcon,
  NumberTicker,
  SettingsIcon,
  ShieldCheckIcon,
  TicketIcon,
} from '@talk2nick/ui';

import type { AuthState } from '../features/auth/useAuth';
import { useAdminClaim } from '../features/auth/useAdminClaim';
import { useUnread } from '../features/chat/unread';
import { useMyInvitations } from '../features/chat/tickets';
import { triggerHaptic } from '../features/ui/haptics';
import { chatTabFromSearch } from '../features/chat/chatTabs';

export function AppBottomNav({ auth }: { auth: AuthState }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const myUid = auth.firebaseUser?.uid ?? null;
  const isAdmin = useAdminClaim(myUid);
  const { total: unreadTotal } = useUnread(myUid);
  const { invitations } = useMyInvitations(isAdmin ? null : (auth.firebaseUser?.email ?? null));
  const pendingInvitesCount = invitations.length;

  const pathname = location.pathname;
  const searchParams = new URLSearchParams(location.search);
  const activeTabParam = searchParams.get('tab');
  const chatTab = chatTabFromSearch(searchParams);

  // Hide in full-screen rooms or join routes to give complete real estate to conversation/flows
  if (
    pathname.startsWith('/chat/') ||
    pathname.startsWith('/join/') ||
    pathname === '/share' ||
    auth.status !== 'authenticated'
  ) {
    return null;
  }

  const isTicketsActive =
    (pathname === '/chats' && (chatTab === 'invites' || chatTab === 'issuedTickets')) ||
    (pathname === '/admin' && activeTabParam === 'tickets');

  const isChatsActive = pathname === '/chats' && !isTicketsActive;
  const isAdminActive = pathname.startsWith('/admin') && !isTicketsActive;
  const isSettingsActive = pathname.startsWith('/settings');

  interface NavItem {
    id: string;
    label: string;
    icon: React.ReactNode;
    active: boolean;
    badge?: number;
    hasDot?: boolean;
    onClick: () => void;
  }

  const items: NavItem[] = [
    {
      id: 'chats',
      label: t('nav.chats') || '채팅',
      active: isChatsActive,
      badge: unreadTotal,
      icon: <MessageSquareIcon size={20} />,
      onClick: () => {
        triggerHaptic('light');
        navigate('/chats');
      },
    },
    {
      id: 'tickets',
      label: isAdmin ? t('admin.tickets') || '티켓' : t('chat.filterInvites') || '초대장',
      active: isTicketsActive,
      badge: isAdmin ? undefined : pendingInvitesCount,
      hasDot: isAdmin ? false : pendingInvitesCount > 0,
      icon: <TicketIcon size={20} />,
      onClick: () => {
        triggerHaptic('light');
        if (isAdmin) {
          navigate('/chats?tab=issuedTickets');
        } else {
          navigate('/chats?tab=invites');
        }
      },
    },
    ...(isAdmin
      ? [
          {
            id: 'admin',
            label: t('admin.console') || '어드민',
            active: isAdminActive,
            icon: <ShieldCheckIcon size={20} />,
            onClick: () => {
              triggerHaptic('light');
              navigate('/admin');
            },
          },
        ]
      : []),
    {
      id: 'settings',
      label: t('settings.title') || '설정',
      active: isSettingsActive,
      icon: auth.profile?.photoURL ? (
        <Avatar
          src={auth.profile.photoURL}
          name={auth.profile.displayName || 'Me'}
          size={22}
          className="ring-1 ring-white/40"
        />
      ) : (
        <SettingsIcon size={20} />
      ),
      onClick: () => {
        triggerHaptic('light');
        navigate('/settings');
      },
    },
  ];

  return (
    <div className="app-bottom-nav">
      <nav
        aria-label="Main Navigation"
        className="pointer-events-auto floating-dock flex items-center justify-around px-2 py-1.5 rounded-full"
      >
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={item.onClick}
            aria-current={item.active ? 'page' : undefined}
            className={cn(
              'pressable-spring relative flex flex-1 flex-col items-center justify-center py-1.5 px-2 rounded-2xl transition-all',
              item.active
                ? 'text-[var(--accent)] font-semibold'
                : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
            )}
          >
            <div className="relative flex items-center justify-center">
              <span
                className={cn(
                  'flex items-center justify-center transition-transform',
                  item.active ? 'scale-110' : 'scale-100',
                )}
              >
                {item.icon}
              </span>

              {/* Badge counter */}
              {item.badge !== undefined && item.badge > 0 && (
                <span className="absolute -top-1.5 -right-2.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--danger)] px-1 text-[10px] font-bold text-white shadow-sm ring-1 ring-[var(--bg-app)]">
                  <NumberTicker value={item.badge} />
                </span>
              )}

              {/* Dot indicator */}
              {item.hasDot && !item.badge && (
                <span className="absolute -top-0.5 -right-1 h-2 w-2 rounded-full bg-[var(--accent)] animate-pulse" />
              )}
            </div>

            <span className="mt-0.5 text-[10px] tracking-tight">{item.label}</span>

            {/* Active Pill Glow Dot */}
            {item.active && (
              <span className="absolute bottom-0.5 h-1 w-3 rounded-full bg-[var(--accent)]" />
            )}
          </button>
        ))}
      </nav>
    </div>
  );
}
